import { useCallback, useRef } from 'react'
import { useOnInView } from 'react-intersection-observer'
import type { SetRequired } from 'type-fest'
import type { ArtworkPreviewFragment } from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { useArtworkGridCols } from 'src/utils/hooks/useArtworkGridCols'
import { useLocation } from 'src/utils/router/route'
import { trackImageForRec } from 'src/utils/tracker'
import type { ArtworkPreviewCardSimpleProps } from './ArtworkPreviewCardSimple'
import ArtworkPreviewCardSimple from './ArtworkPreviewCardSimple'

export default function ArtworkTrackedPreviewCard(
  props: SetRequired<ArtworkPreviewCardSimpleProps, 'id' | 'index'>,
) {
  const totalCols = useArtworkGridCols()
  const { index, id, artwork, onArtworkClick, ...rest } = props

  const location = useLocation()

  const feed = props.trackingArgs?.feed || 'trending'
  const feedUuid = props.trackingArgs?.feedUuid

  // Tracks whether the element is currently considered in-view.
  // Prevents re-firing when a re-render resets the observer while the card
  // is still visible, but allows re-firing when the user scrolls away then scrolls back.
  const isInViewRef = useRef(false)

  const ref = useOnInView(
    inView => {
      if (!inView) {
        isInViewRef.current = false
        return
      }
      if (isInViewRef.current) return
      isInViewRef.current = true
      trackImageForRec(
        'reco_artwork_view',
        {
          artworkId: id,
          index,
          currentLikeCount: artwork?.likedCount ?? 0,
          scene: 'artwork_feed', // TODO: Hardcoded for now
          feed,
          totalCols,
          hidePrompts: artwork?.hidePrompts ?? false,
          retrieverId: artwork?.feedMetadata?.retrieverId,
          pathname: location.pathname,
        },
        {
          feedUuid,
        },
      )
    },
    {
      threshold: 1,
    },
  )

  const hijackedArtworkClick = useCallback(
    (artwork?: ArtworkPreviewFragment) => {
      trackImageForRec(
        'reco_artwork_click',
        {
          artworkId: id,
          index,
          currentLikeCount: artwork?.likedCount ?? 0,
          scene: 'artwork_feed', // TODO: Hardcoded for now
          feed,
          totalCols,
          hidePrompts: artwork?.hidePrompts ?? false,
          retrieverId: artwork?.feedMetadata?.retrieverId,
        },
        {
          feedUuid,
        },
      )
      onArtworkClick?.(artwork, index)
    },
    [id, index, feed, totalCols, onArtworkClick, feedUuid],
  )

  const onLikeClickForRec = useCallback(() => {
    trackImageForRec(
      'like_click',
      {
        artworkId: id,
        index,
        currentLikeCount: artwork?.likedCount ?? 0,
        scene: 'artwork_feed', // TODO: Hardcoded for now
        feed,
        totalCols,
        type: RefType.Artwork,
        likeFrom: 'grid',
        hidePrompts: artwork?.hidePrompts ?? false,
        retrieverId: artwork?.feedMetadata?.retrieverId,
      },
      { feedUuid },
    )
  }, [
    id,
    index,
    artwork?.likedCount,
    artwork?.feedMetadata?.retrieverId,
    feed,
    totalCols,
    feedUuid,
  ])

  return (
    <ArtworkPreviewCardSimple
      {...rest}
      artwork={artwork}
      onArtworkClick={hijackedArtworkClick}
      onLikeClickForRec={onLikeClickForRec}
      wrapperRef={ref}
    />
  )
}
